import { useSyncExternalStore } from 'react'
import { PALETTE } from './editing'
import { copies, forPaste } from './multi'
import { placeOnScreen } from './pages'
import type { LayoutDocument, LayoutElement, LayoutKind, Screen } from './types'

/** Copied elements, kept for this tab across designs; never the system clipboard. */
export type Clip = { kind: LayoutKind; elements: LayoutElement[]; into: string; pastes: number }

/** How far each paste into the same design lands from the last. */
export const PASTE_STEP = 20

let clip: Clip | null = null
const listeners = new Set<() => void>()

function changed() {
  for (const l of listeners) l()
}

export function copyElements(kind: LayoutKind, elements: readonly LayoutElement[]): void {
  clip = elements.length > 0 ? { kind, elements: structuredClone([...elements]), into: '', pastes: 0 } : null
  changed()
}

export function currentClip(): Clip | null {
  return clip
}

export function clearClip(): void {
  clip = null
  changed()
}

function subscribe(fn: () => void) {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export function useClip(): Clip | null {
  return useSyncExternalStore(subscribe, currentClip, currentClip)
}

/**
 * Copies of copied elements for this design, offset and with new ids. Into the same
 * kind of design every copied element may go; into another, only kinds its palette
 * offers, placed on the screen being drawn. What cannot go is counted in `left`.
 */
export function pasted(
  from: Pick<Clip, 'kind' | 'elements'>,
  doc: LayoutDocument,
  screen: Screen | undefined,
  page: string | undefined,
  offset: number,
): { made: LayoutElement[]; left: number } {
  const same = from.kind === doc.kind
  const fit = same ? from.elements : from.elements.filter((el) => PALETTE[doc.kind].includes(el.kind))
  const placed = fit.map((el) => (same ? forPaste(el, doc, screen, page) : across(el, doc, screen, page)))
  const made = copies(doc, placed, offset)
  return { made, left: from.elements.length - made.length }
}

/** Another kind's screens, pages and button action mean nothing here. */
function across(el: LayoutElement, doc: LayoutDocument, screen: Screen | undefined, page: string | undefined): LayoutElement {
  const { screens: _s, pages: _p, action: _a, ...plain } = el
  return doc.kind === 'display' ? forPaste(plain, doc, screen, page) : placeOnScreen(plain, doc, screen, page)
}

/** Pastes the clip into a design: each paste into the same design a step further on. */
export function pasteClip(
  doc: LayoutDocument,
  layoutId: string,
  screen: Screen | undefined,
  page: string | undefined,
): { made: LayoutElement[]; left: number } | null {
  if (!clip) return null
  const n = clip.into === layoutId ? clip.pastes + 1 : 1
  const out = pasted(clip, doc, screen, page, PASTE_STEP * n)
  if (out.made.length > 0) {
    clip.into = layoutId
    clip.pastes = n
  }
  return out
}
